import { InfoCircle, Locked, WarningSolid } from '@wfp/icons-react';
import {
  Button,
  Modal,
  Module,
  ModuleBody,
  ModuleFooter,
  ModuleHeader,
  Tag,
} from '@wfp/react';
import { format } from 'date-fns';
import DOMPurify from 'dompurify';
import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ConfirmationModal from 'src/components/ConfirmationModal';
import AnchorModal from 'src/components/ReportTemplate/anchorModal';
import {
  AI_EXTENDED_VALID_ELEMENTS,
  AI_HIGHLIGHT_CONTENT_STYLE,
  AI_PREVIEW_OLD_SPAN_CLASS,
  AI_PREVIEW_SPAN_CLASS,
  REPHRASE_ACTIONS,
} from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/constants';
import DraftGenerationSkeleton from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/DraftGenerationSkeleton';
import DraftPreviewPanel from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/DraftPreviewPanel';
import GenerateDraftButton from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/GenerateDraftButton';
import RephraseLoadingChip from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/RephraseLoadingChip';
import RephrasePreviewControls from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/RephrasePreviewControls';
import RephraseToolbar from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/RephraseToolbar';
import useEditorSelection from 'src/components/ReportTemplate/WorkspaceACR2023/Narrative/AiAssist/useEditorSelection';
import TinyEditorWrapper from 'src/components/ReportTemplate/WorkspaceACR2023/TinyEditorWrapper';
import VersionItem from 'src/components/ReportTemplate/WorkspaceACR2023/VersionItem';
import VersionsModal from 'src/components/ReportTemplate/WorkspaceACR2023/VersionsModal';
import {
  API_HOST,
  narrativeAutosaveInterval,
  SUCCESS_REQUEST,
  UPDATE,
} from 'src/constants';
import {
  acceptPreview,
  clearPreview,
  rejectPreview,
  rephraseRequest,
} from 'src/redux/aiNarrative/slice';
import {
  fetchTemplatesRequest,
  resetTemplates,
  selectPromptTemplates,
} from 'src/redux/aiNarrative/templatesSlice';
import {
  AiNarrativeContext,
  RephraseActionKind,
} from 'src/redux/aiNarrative/types';
import {
  getPlainText,
  hasPromptTemplateParams,
  promptTemplateKey,
  promptTemplateParams,
  stripSourceRefs,
} from 'src/redux/aiNarrative/utils';
import { requestAPI, requestAPIHoldingData } from 'src/redux/api/actions';
import { apiCallAsync } from 'src/redux/api/saga';
import { sendSignalsAction } from 'src/redux/componentSignals/actions';
import { COMMENTS_LIST, COMMENTS_TARGET_ID } from 'src/redux/constants';
import {
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
} from 'src/redux/constants';
import {
  cancelMultiNarrative,
  forceUnlockMultiNarrative,
  lockMultiNarrative,
  receiveMultiNarrativeSingleResponse,
  resetMultiNarrative,
  restoreVersionMultiNarrative,
  saveMultiNarrative,
} from 'src/redux/narrative/actions';
import { RootState } from 'src/redux/store';
import { changeViewStateAction } from 'src/redux/viewState/actions';
import type { INarrative, ISelectedVersion } from 'src/types';
import { countWords, sendGAEvent, showNotification } from 'src/utils';

interface Props {
  narrative: INarrative;
  isEditEnable: boolean;
  isForcePostEnabled: boolean;
}

const NarrativeErrorMessage = ({
  wordsCountResume,
}: {
  wordsCountResume: string;
}) => {
  return (
    <div className="narrative-footer narrative-error-msg">
      <WarningSolid color="#E4002B" />
      <div>Word count limit exceed: {wordsCountResume}</div>
    </div>
  );
};

const Narrative: FC<Props> = ({
  narrative,
  isEditEnable,
  isForcePostEnabled,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedVersion, setSelectedVersion] =
    useState<ISelectedVersion | null>(null);

  const [autosave, setAutosave] = useState(false);
  const editorRef = React.useRef(null);

  const [rephraseSnapshot, setRephraseSnapshot] = useState<string | null>(null);
  const [rephraseControlsPos, setRephraseControlsPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [rephraseLoadingPos, setRephraseLoadingPos] = useState<{
    top: number;
    left: number;
    placement: 'top' | 'bottom';
  } | null>(null);
  const rephraseInsertedRef = React.useRef(false);

  const viewState = useSelector((state: RootState) => state.viewState);
  const reportDetailData = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY].data,
  );
  const sectionDetailData = useSelector(
    (state: RootState) => state.viewData?.[SECTION_DETAIL_DATA_KEY]?.data,
  );
  const multinarrativeIsFetching = useSelector(
    (state: RootState) =>
      state.viewData[SECTION_DETAIL_DATA_KEY].isFetchingMultinarrative,
  );
  const aiPreview = useSelector(
    (state: RootState) => state.aiNarrative[narrative.id],
  );

  const dispatch = useDispatch();

  const {
    id,
    is_optional: isOptional,
    limit,
    title,
    i_am_editor: iAmEditor,
    content: initialContent,
    editor,
    locked_at: lockedAt,
    saved = false,
    autosaved = false,
    canceled = false,
    locked = false,
    info,
    narrative_type: narrativeType,
  } = narrative;

  const [content, setContent] = useState(initialContent || '');

  const getCurrentWordCount = () => {
    return countWords(getPlainText(content));
  };

  const wordsExceed = getCurrentWordCount() > limit;

  const narrativeEditedByOthers = !iAmEditor && editor !== null;
  const imEditing = isEdit || iAmEditor;
  const notPublished = reportDetailData?.state !== 'Published';

  const aiStatus = aiPreview?.status ?? 'idle';
  const aiMode = aiPreview?.mode ?? null;
  const aiDraftLoading = aiMode === 'draft' && aiStatus === 'loading';
  const aiDraftReady = aiMode === 'draft' && aiStatus === 'ready';
  const aiRephraseLoading = aiMode === 'rephrase' && aiStatus === 'loading';
  const aiRephraseReady = aiMode === 'rephrase' && aiStatus === 'ready';
  // While an AI request or preview is pending, nothing may be saved:
  // Cancel/Save are disabled and autosave is suppressed.
  const aiBusy = aiStatus === 'loading' || aiStatus === 'ready';
  const aiBusyRef = React.useRef(aiBusy);
  aiBusyRef.current = aiBusy;

  const aiContext: AiNarrativeContext = useMemo(
    () => ({
      narrativeType: narrativeType || '',
      sectionTitle: sectionDetailData?.current_title || '',
      narrativeTitle: title,
      wordLimit: limit,
      country: reportDetailData?.country,
      period: reportDetailData?.period,
      projectCode: reportDetailData?.project_code,
    }),
    [
      narrativeType,
      sectionDetailData?.current_title,
      title,
      limit,
      reportDetailData?.country,
      reportDetailData?.period,
      reportDetailData?.project_code,
    ],
  );

  const templateParams = useMemo(
    () => promptTemplateParams(aiContext),
    [aiContext],
  );
  const promptTemplates = useSelector(
    selectPromptTemplates(promptTemplateKey(templateParams)),
  );
  const aiAvailable =
    promptTemplates.status === 'ready' && promptTemplates.items.length > 0;

  const { selection: rephraseSelection, clearSelection } = useEditorSelection(
    editorRef,
    isEdit && !aiBusy && notPublished && aiAvailable,
  );

  // Only from idle: retrying from the error state would loop through this effect.
  useEffect(() => {
    if (
      isEdit &&
      promptTemplates.status === 'idle' &&
      hasPromptTemplateParams(templateParams)
    ) {
      dispatch(
        fetchTemplatesRequest({
          key: promptTemplateKey(templateParams),
          params: templateParams,
        }),
      );
    }
  }, [isEdit, promptTemplates.status, templateParams]);

  const isFetchingCurrentNarrative = useMemo(
    () => multinarrativeIsFetching && multinarrativeIsFetching === id,
    [multinarrativeIsFetching, id],
  );

  useEffect(() => {
    if (iAmEditor) setIsEdit(true);
  }, [iAmEditor]);

  useEffect(() => {
    if (saved) {
      setIsEdit(false);
      dispatch(resetMultiNarrative({ narrativeId: id }));
    }
  }, [saved]);

  useEffect(() => {
    if (canceled) {
      setIsEdit(false);
      setContent(initialContent);
      dispatch(resetMultiNarrative({ narrativeId: id }));
    }
  }, [canceled]);

  useEffect(() => {
    if (autosaved) {
      showNotification(`Auto save applied for ${title}`, 'success', {
        autoClose: 3500,
      });
      dispatch(resetMultiNarrative({ narrativeId: id }));
    }
  }, [autosaved]);

  useEffect(() => {
    if (locked) {
      setIsEdit(true);
      dispatch(resetMultiNarrative({ narrativeId: id }));
    }
  }, [locked]);

  useEffect(() => {
    if (selectedVersion !== null) setShowVersionsModal(false);
  }, [selectedVersion]);

  useEffect(() => {
    if (!isEdit) {
      setContent(initialContent);
    }
  }, [initialContent, isEdit]);

  function resetRephraseState() {
    setRephraseSnapshot(null);
    setRephraseControlsPos(null);
    setRephraseLoadingPos(null);
    rephraseInsertedRef.current = false;
  }

  // Once the rephrased text is ready, replace the selection with a
  // highlighted preview span and lock the editor until Accept/Reject.
  useEffect(() => {
    const tinyEditor = editorRef.current;
    if (!aiRephraseReady || !tinyEditor || rephraseInsertedRef.current) return;

    const proposedText = aiPreview?.rephrase?.proposedText || '';
    const originalText = aiPreview?.rephrase?.originalText || '';
    tinyEditor.mode.set('design');
    // Keep the original selection visible (struck through) right before the AI
    // proposal so the user always sees the text prior to the AI change.
    tinyEditor.undoManager.transact(() => {
      tinyEditor.selection.setContent(
        `<span class="${AI_PREVIEW_OLD_SPAN_CLASS}" data-ai-preview="rephrase-old">${tinyEditor.dom.encode(originalText)}</span>` +
          `<span class="${AI_PREVIEW_SPAN_CLASS}" data-ai-preview="rephrase">${tinyEditor.dom.encode(proposedText)}</span>`,
      );
    });
    tinyEditor.mode.set('readonly');
    rephraseInsertedRef.current = true;
    setRephraseLoadingPos(null);

    const previewSpan = tinyEditor
      .getBody()
      .querySelector(`span.${AI_PREVIEW_SPAN_CLASS}`);
    if (previewSpan && tinyEditor.iframeElement) {
      const spanRect = previewSpan.getBoundingClientRect();
      const iframeRect = tinyEditor.iframeElement.getBoundingClientRect();
      setRephraseControlsPos({
        top: iframeRect.top + spanRect.bottom + window.scrollY + 10,
        left:
          iframeRect.left + spanRect.left + spanRect.width / 2 + window.scrollX,
      });
    }
  }, [aiRephraseReady]);

  // A failed AI request must never leave the editor locked or the preview
  // stuck: the saga already notified the user, here we just reset.
  useEffect(() => {
    if (aiStatus !== 'error') return;

    if (aiMode === 'rephrase') {
      editorRef.current?.mode.set('design');
      resetRephraseState();
    }
    dispatch(clearPreview({ narrativeId: id }));
  }, [aiStatus]);

  useEffect(() => {
    if (!isEdit) {
      dispatch(clearPreview({ narrativeId: id }));
      resetRephraseState();
      // Leaving edit mode is the retry point for a failed template load.
      if (promptTemplates.status === 'error') {
        dispatch(resetTemplates({ key: promptTemplateKey(templateParams) }));
      }
    }
  }, [isEdit]);

  useEffect(() => {
    return () => {
      dispatch(clearPreview({ narrativeId: id }));
    };
  }, []);

  function handleCancel() {
    dispatch(
      cancelMultiNarrative({
        narrativeId: id,
        reportId: sectionDetailData?.report?.id,
      }),
    );
    setShowCancelModal(false);
  }

  function handleSave() {
    setShowSaveModal(false);

    const wordCount = getCurrentWordCount();

    dispatch(
      saveMultiNarrative({
        narrativeId: id,
        content,
        wordCount,
        autoSave: false,
        reportId: sectionDetailData?.report?.id,
      }),
    );
  }

  function handleRevertVersion() {
    dispatch(
      restoreVersionMultiNarrative({
        narrativeVersionId: selectedVersion.id,
        content: selectedVersion.cleanContent,
        narrativeId: id,
      }),
    );
    setSelectedVersion(null);
  }

  function handleEditBtn() {
    sendGAEvent('edit_narrative_btn_clicked');
    dispatch(
      lockMultiNarrative({
        narrativeId: id,
        reportId: sectionDetailData?.report?.id,
      }),
    );
  }

  function handleForceEditBtn() {
    sendGAEvent('force_post_narrative_btn_clicked');
    dispatch(
      forceUnlockMultiNarrative({
        narrativeId: id,
        reportId: sectionDetailData?.report?.id,
      }),
    );
  }

  function handleDraftAccept() {
    if (!aiPreview?.draftHtml) return;

    setContent(stripSourceRefs(aiPreview.draftHtml));
    setAutosave(true);
    dispatch(acceptPreview({ narrativeId: id }));
  }

  function handleDraftReject() {
    dispatch(rejectPreview({ narrativeId: id }));
  }

  function handleRephraseAction(action: RephraseActionKind) {
    const tinyEditor = editorRef.current;
    if (!tinyEditor || !rephraseSelection) return;

    sendGAEvent('ai_rephrase_btn_clicked');
    setRephraseSnapshot(content);
    setRephraseLoadingPos({
      top: rephraseSelection.top,
      left: rephraseSelection.left,
      placement: rephraseSelection.placement,
    });
    rephraseInsertedRef.current = false;
    // Restore the range captured at selection time: clicking the floating
    // toolbar may have moved focus out of the editor iframe.
    tinyEditor.selection.setRng(rephraseSelection.rng);
    tinyEditor.mode.set('readonly');
    dispatch(
      rephraseRequest({
        narrativeId: id,
        selectedText: rephraseSelection.text,
        action,
        context: aiContext,
      }),
    );
    clearSelection();
  }

  function handleRephraseAccept() {
    const tinyEditor = editorRef.current;
    if (tinyEditor) {
      tinyEditor.mode.set('design');
      tinyEditor.undoManager.transact(() => {
        // Drop the original (before) text entirely, keep the AI proposal.
        tinyEditor.dom
          .select(`span.${AI_PREVIEW_OLD_SPAN_CLASS}`)
          .forEach(node => tinyEditor.dom.remove(node));
        tinyEditor.dom
          .select(`span.${AI_PREVIEW_SPAN_CLASS}`)
          .forEach(node => tinyEditor.dom.remove(node, true));
      });
      setContent(tinyEditor.getContent());
      setAutosave(true);
    }
    resetRephraseState();
    dispatch(acceptPreview({ narrativeId: id }));
  }

  function handleRephraseReject() {
    const tinyEditor = editorRef.current;
    if (tinyEditor && rephraseSnapshot !== null) {
      tinyEditor.mode.set('design');
      tinyEditor.undoManager.transact(() => {
        tinyEditor.setContent(rephraseSnapshot);
      });
      setContent(rephraseSnapshot);
    }
    resetRephraseState();
    dispatch(rejectPreview({ narrativeId: id }));
  }

  function getWordsCountResume() {
    const wordsCount = getCurrentWordCount();

    return `${wordsCount || 0} / ${limit}`;
  }

  function createComment(
    anchorName: string,
    anchorID: number,
    anchorContent: string,
  ) {
    const params = {
      url_format: {
        _office_: sectionDetailData.report.office_code,
        _report_: sectionDetailData.report.id,
        _section_: viewState.activeSectionID,
      },
      method: 'post',
      data: {
        content: anchorContent,
        title: anchorName,
        anchor: anchorName,
        anchor_id: anchorID,
      },
      storage_key: `${COMMENTS_LIST}_created`,
      success_actions: [
        sendSignalsAction({
          [undefined as any]: {
            [UPDATE]: true,
            [SUCCESS_REQUEST]: true,
          },
        }),
        requestAPIHoldingData(REPORT_DETAIL_DATA_KEY, null, {
          url_format: { _id_: sectionDetailData.report.id },
        }),
      ],
    };

    dispatch(requestAPI(COMMENTS_LIST, params));
  }

  function onCommentAttachedToNarrative() {
    const requestKwargs = {
      url_format: {
        _office_: sectionDetailData.report.office_code,
        _report_: sectionDetailData.report.id,
        _section_: viewState.activeSectionID,
      },
      storage_key: COMMENTS_LIST,
    };
    dispatch(requestAPI(COMMENTS_LIST, requestKwargs));

    dispatch(
      sendSignalsAction({
        [COMMENTS_TARGET_ID]: { [UPDATE]: true },
      }),
    );
  }

  async function performAnchorAddingRequest(
    value: string,
    anchorName: string,
    anchorID: number,
    anchorComment: string,
    narrativeId?: number,
  ) {
    const url = `${API_HOST}/api/acr-sections/${viewState.activeSectionID}/add_anchors/`;
    const request_body = {
      content: value,
      editor: reportDetailData.username,
      narrative_id: narrativeId,
      title: anchorName,
    };

    try {
      const result = await apiCallAsync(url, request_body, 'post');

      dispatch(receiveMultiNarrativeSingleResponse(result.data));

      createComment(anchorName, anchorID, anchorComment);

      const requestKwargs = {
        url_format: {
          _office_: sectionDetailData.report.office_code,
          _report_: sectionDetailData.report.id,
          _section_: viewState.activeSectionID,
        },
        storage_key: COMMENTS_LIST,
      };

      dispatch(requestAPI(COMMENTS_LIST, requestKwargs));

      onCommentAttachedToNarrative();
      dispatch(changeViewStateAction({ draftComment: undefined }));
    } catch (e) {
      const msg = e.response?.data?.error;
      if (!msg) return;

      showNotification(msg, 'error');
    }
  }

  function onNarrativeChange(value: string) {
    setContent(value);
    // Preview-driven editor changes must never arm autosave: the user has
    // to accept or reject the AI proposal before anything is saved.
    if (!autosave && !aiBusyRef.current) setAutosave(true);
  }

  useEffect(() => {
    if (!autosave) {
      return;
    }

    const timer = setTimeout(() => {
      if (!isEdit || aiBusyRef.current) {
        return;
      }

      const wordCount = getCurrentWordCount();

      dispatch(
        saveMultiNarrative({
          narrativeId: id,
          content,
          wordCount,
          autoSave: true,
        }),
      );
    }, narrativeAutosaveInterval);

    return () => clearTimeout(timer);
  }, [autosave, content, isEdit]);

  return (
    <>
      <Module
        className="multi-narrative-section"
        data-test-id={title.toLowerCase().split(' ').join('-')}
      >
        <ModuleHeader>
          <div className="multi-narrative-section-title">
            {title}
            {info && (
              <InfoCircle
                className="word-limit-panel-icon-info"
                width="24"
                height="24"
                color="#0b77c2"
                description=""
                data-test-id="information-icon"
                onClick={() => setShowInfoModal(true)}
              />
            )}
            {isOptional && <Tag type="info">Optional</Tag>}
          </div>

          <div className="multi-narrative-section-btns">
            <Button
              onClick={() => {
                setAutosave(false);
                sendGAEvent('narrative_versions_btn_clicked');
                setShowVersionsModal(true);
              }}
              kind="ghost"
              data-test-id="versions-button"
            >
              Versions
            </Button>
            {isEdit && (
              <>
                <GenerateDraftButton
                  narrativeId={id}
                  currentContent={content}
                  context={aiContext}
                  isLoading={aiDraftLoading}
                  disabled={
                    aiBusy || isFetchingCurrentNarrative || !aiAvailable
                  }
                />
                <Button
                  onClick={() => {
                    setAutosave(false);
                    setShowCancelModal(true);
                  }}
                  kind="secondary"
                  disabled={aiBusy}
                  data-test-id="cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setAutosave(false);
                    setShowSaveModal(true);
                  }}
                  disabled={wordsExceed || isFetchingCurrentNarrative || aiBusy}
                  data-test-id="save-version-button"
                >
                  Save version
                </Button>
              </>
            )}
            {!isEdit &&
              !narrativeEditedByOthers &&
              isEditEnable &&
              notPublished && (
                <Button
                  data-test-id="edit-button"
                  onClick={handleEditBtn}
                  disabled={isFetchingCurrentNarrative}
                >
                  Edit
                </Button>
              )}
            {!isEdit &&
              narrativeEditedByOthers &&
              notPublished &&
              isForcePostEnabled && (
                <Button
                  data-test-id="force-edit-button"
                  onClick={handleForceEditBtn}
                  disabled={isFetchingCurrentNarrative}
                >
                  Force Edit
                </Button>
              )}
          </div>
        </ModuleHeader>
        <ModuleBody>
          {isEdit && aiDraftLoading && <DraftGenerationSkeleton />}
          {isEdit && !aiDraftLoading && aiDraftReady && aiPreview?.draftHtml ? (
            <DraftPreviewPanel
              draftHtml={aiPreview.draftHtml}
              currentContent={content}
              references={aiPreview.references || []}
              wordLimit={limit}
              hasExistingContent={!!getPlainText(content).trim()}
              onAccept={handleDraftAccept}
              onReject={handleDraftReject}
            />
          ) : (
            isEdit &&
            !aiDraftLoading && (
              <TinyEditorWrapper
                content={content}
                onChange={onNarrativeChange}
                editorRef={editorRef}
                extendedValidElements={AI_EXTENDED_VALID_ELEMENTS}
                extraContentStyle={AI_HIGHLIGHT_CONTENT_STYLE}
              />
            )
          )}
          {notPublished &&
            !isEdit &&
            !narrativeEditedByOthers &&
            reportDetailData?.permissions?.comment_permissions?.add_issue &&
            (!content ? (
              <div className="narrative-empty-info">No narrative</div>
            ) : (
              <AnchorModal
                sectionId={viewState.activeSectionID}
                text={content}
                performAnchorAddingRequest={performAnchorAddingRequest}
                isExtra={false}
                narrativeId={id}
              />
            ))}
          {!isEdit &&
            (!notPublished ||
              narrativeEditedByOthers ||
              !reportDetailData?.permissions?.comment_permissions?.add_issue) &&
            (!content ? (
              <div className="narrative-empty-info">No narrative</div>
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(content),
                }}
              />
            ))}
        </ModuleBody>
        <ModuleFooter
          className={`${
            imEditing || narrativeEditedByOthers ? 'edit-mode' : ''
          }`}
        >
          {imEditing && lockedAt && (
            <div className="narrative-locked-msg">
              <Locked color="#04193B" />
              Narrative locked by you on
              {` ${format(lockedAt, 'dd MMMM yyyy, HH:mm')} (CEST)`}. It is not
              editable by others.
            </div>
          )}
          {narrativeEditedByOthers && lockedAt && (
            <div className="narrative-locked-msg">
              <Locked color="#04193B" />
              Narrative locked by {editor} on
              {` ${format(lockedAt, 'dd MMMM yyyy, HH:mm')} (CEST)`}. It is not
              editable by others.
            </div>
          )}
          {aiRephraseReady && (
            <div className="narrative-footer ai-preview-hint">
              Review the AI suggestion (Accept or Reject) to continue editing.
            </div>
          )}
          {wordsExceed ? (
            <NarrativeErrorMessage wordsCountResume={getWordsCountResume()} />
          ) : (
            <div data-test-id="word-counter" className="narrative-footer">
              Word count: {getWordsCountResume()}
            </div>
          )}
        </ModuleFooter>
      </Module>
      {showVersionsModal && (
        <VersionsModal
          title={title}
          onClose={() => setShowVersionsModal(false)}
          narrativeId={id}
          setSelectedVersion={setSelectedVersion}
          revertEnabled={
            !narrativeEditedByOthers && isEditEnable && !isEdit && notPublished
          }
        />
      )}
      {selectedVersion && (
        <ConfirmationModal
          open={true}
          onSubmit={handleRevertVersion}
          onClose={() => setSelectedVersion(null)}
          heading={`Revert to this version for ${title}?`}
          content={
            <>
              <div className="revert-version-subtitle">
                Are you sure you want to revert the narrative to this version?
              </div>
              <VersionItem
                content={selectedVersion.content}
                cleanContent={selectedVersion.cleanContent}
                editor={selectedVersion.editor}
                date={selectedVersion.date}
                id={selectedVersion.id}
              />
            </>
          }
          primaryButtonText="Yes"
          secondaryButtonText="No"
          wide
          customClassName="modal-revert-version"
        />
      )}
      {showSaveModal && (
        <ConfirmationModal
          open={true}
          onSubmit={handleSave}
          onClose={() => setShowSaveModal(false)}
          heading={`Save as a new version for ${title}?`}
          content={
            <>
              Are you sure you want to save your changes as a new version of
              this narrative?
            </>
          }
          primaryButtonText="Yes"
          secondaryButtonText="No"
        />
      )}
      {showCancelModal && (
        <ConfirmationModal
          open={true}
          onSubmit={handleCancel}
          onClose={() => setShowCancelModal(false)}
          heading={`Discard changes for ${title}?`}
          content={
            <>
              Are you sure you want to cancel your changes and go back to the
              previous version of this narrative?
            </>
          }
          primaryButtonText="Yes"
          secondaryButtonText="No"
        />
      )}
      <Modal
        open={showInfoModal}
        onRequestClose={() => setShowInfoModal(false)}
        modalHeading={title}
        passiveModal
        iconDescription="Close the modal"
        className="info-text-modal"
      >
        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(info) }} />
      </Modal>
      {rephraseSelection && (
        <RephraseToolbar
          top={rephraseSelection.top}
          left={rephraseSelection.left}
          placement={rephraseSelection.placement}
          onAction={handleRephraseAction}
        />
      )}
      {aiRephraseLoading && rephraseLoadingPos && (
        <RephraseLoadingChip
          top={rephraseLoadingPos.top}
          left={rephraseLoadingPos.left}
          placement={rephraseLoadingPos.placement}
          actionLabel={
            REPHRASE_ACTIONS.find(
              action => action.kind === aiPreview?.rephrase?.action,
            )?.label || ''
          }
        />
      )}
      {aiRephraseReady && rephraseControlsPos && (
        <RephrasePreviewControls
          top={rephraseControlsPos.top}
          left={rephraseControlsPos.left}
          actionLabel={
            REPHRASE_ACTIONS.find(
              action => action.kind === aiPreview?.rephrase?.action,
            )?.label || ''
          }
          onAccept={handleRephraseAccept}
          onReject={handleRephraseReject}
        />
      )}
      <div className="ai-sr-only" aria-live="polite" role="status">
        {aiDraftLoading && 'Generating AI draft'}
        {aiDraftReady && 'AI draft ready for review'}
        {aiRephraseLoading && 'Rephrasing selected text'}
        {aiRephraseReady && 'AI suggestion ready for review'}
      </div>
    </>
  );
};

export default Narrative;
